import React from 'react';
import { useTranslation } from 'react-i18next';

import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TransferPanel } from '@/components/ui/panels/TransferPanel';
import { PermissionGuard } from '@/components/layout';
import { useAssignAdministratorsTab } from '@/hooks/securityGrouping';


export const AssignAdministratorsTab: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    groupingOptions,
    selectedGroupId,
    onGroupChange,
    available,
    filteredAvailable,
    isAvailableLoading,
    hasMoreAvailable,
    totalAvailable,
    availableSearchText,
    onAvailableSearch,
    availableSentinelRef,
    availableScrollContainerRef,
    assigned,
    filteredAssigned,
    isAssignedLoading,
    hasMoreAssigned,
    totalAssigned,
    assignedSearchText,
    onAssignedSearch,
    assignedSentinelRef,
    assignedScrollContainerRef,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickAvailable,
    onDoubleClickAssigned,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    canEdit,
    onSave,
    onCancel,
  } = useAssignAdministratorsTab();

  return (
    <div className="flex flex-col gap-6" data-testid="assign-administrators-tab">
      <div className="w-96">
        <Selector
          label={translate('securityGrouping:assignAdministrators.groupingLabel')}
          placeholder={translate('securityGrouping:assignAdministrators.groupingPlaceholder')}
          options={groupingOptions}
          value={selectedGroupId ? String(selectedGroupId) : undefined}
          onChange={(value) => onGroupChange(value ? Number(value) : null)}
          data-testid="assign-administrators-grouping-selector"
        />
      </div>

      <TransferPanel
        keyExtractor={(employee) => employee.employeeId}
        renderTitle={(employee) => employee.employeeName}
        renderSubtitle={(employee) => employee.employeeTaxIdentificationNumber}
        leftPanel={{
          title: translate('securityGrouping:assignAdministrators.availableTitle'),
          items: filteredAvailable,
          allItems: available,
          isLoading: isAvailableLoading,
          totalRecords: totalAvailable,
          searchValue: availableSearchText,
          onSearch: onAvailableSearch,
          searchPlaceholder: translate('securityGrouping:assignAdministrators.search'),
          selectedIds: selectedAvailableIds,
          onToggleSelect: onToggleAvailable,
          onSelectAll: onSelectAllAvailable,
          isAllSelected: isAllAvailableSelected,
          isIndeterminate: isAvailableIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAvailable : () => {},
          onBulkAction: (isRemoveAllPending || !canEdit) ? undefined : onAddAllFromServer,
          bulkActionMessage: translate(
            'securityGrouping:assignAdministrators.addAllBannerMessage',
            { total: totalAvailable },
          ),
          sentinelRef: availableSentinelRef,
          scrollContainerRef: availableScrollContainerRef,
          selectAllHint: hasMoreAvailable
            ? translate('securityGrouping:assignAdministrators.selectAllHint', { loaded: available.length })
            : undefined,
          footerText: (isRemoveAllPending && hasMoreAvailable)
            ? translate('securityGrouping:assignAdministrators.footerSavePending', {
              pending: totalAvailable - available.length,
            })
            : hasMoreAvailable
              ? translate('securityGrouping:assignAdministrators.footerPartial', {
                selected: selectedAvailableIds.size,
                loaded: available.length,
                total: totalAvailable,
              })
              : translate('securityGrouping:assignAdministrators.footer', {
                selected: selectedAvailableIds.size,
                total: available.length,
              }),
          'data-testid': 'available-admins-panel',
        }}
        rightPanel={{
          title: translate('securityGrouping:assignAdministrators.assignedTitle'),
          items: filteredAssigned,
          allItems: assigned,
          isLoading: isAssignedLoading,
          totalRecords: totalAssigned,
          searchValue: assignedSearchText,
          onSearch: onAssignedSearch,
          searchPlaceholder: translate('securityGrouping:assignAdministrators.search'),
          selectedIds: selectedAssignedIds,
          onToggleSelect: onToggleAssigned,
          onSelectAll: onSelectAllAssigned,
          isAllSelected: isAllAssignedSelected,
          isIndeterminate: isAssignedIndeterminate,
          onDoubleClick: canEdit ? onDoubleClickAssigned : () => {},
          onBulkAction: (isAddAllPending || !canEdit) ? undefined : onRemoveAllFromServer,
          bulkActionMessage: translate(
            'securityGrouping:assignAdministrators.removeAllBannerMessage',
            { total: totalAssigned },
          ),
          sentinelRef: assignedSentinelRef,
          scrollContainerRef: assignedScrollContainerRef,
          selectAllHint: hasMoreAssigned
            ? translate('securityGrouping:assignAdministrators.selectAllHint', { loaded: assigned.length })
            : undefined,
          footerText: (isAddAllPending && hasMoreAssigned)
            ? translate('securityGrouping:assignAdministrators.footerSavePending', {
              pending: totalAssigned - assigned.length,
            })
            : hasMoreAssigned
              ? translate('securityGrouping:assignAdministrators.footerPartial', {
                selected: selectedAssignedIds.size,
                loaded: assigned.length,
                total: totalAssigned,
              })
              : translate('securityGrouping:assignAdministrators.footer', {
                selected: selectedAssignedIds.size,
                total: assigned.length,
              }),
          'data-testid': 'assigned-admins-panel',
        }}
        onAdd={onAdd}
        onRemove={onRemove}
        addLabel={translate('securityGrouping:assignAdministrators.addButton')}
        removeLabel={translate('securityGrouping:assignAdministrators.removeButton')}
        addAllLabel={translate('securityGrouping:assignAdministrators.addAll')}
        removeAllLabel={translate('securityGrouping:assignAdministrators.removeAll')}
        isAddDisabled={selectedAvailableIds.size === 0 || !canEdit}
        isRemoveDisabled={selectedAssignedIds.size === 0 || !canEdit}
        data-testid="assign-administrators-transfer"
      />

      <div className="flex justify-center gap-3 pt-2">
        <PrimaryButton
          variant="outline"
          onClick={onCancel}
          disabled={!isDirty || isSaving}
          data-testid="assign-administrators-cancel-button"
        >
          {translate('securityGrouping:assignAdministrators.cancel')}
        </PrimaryButton>
        <PermissionGuard action="edit">
          <PrimaryButton
            onClick={() => { void onSave(); }}
            isLoading={isSaving}
            disabled={!isDirty || isSaving}
            data-testid="assign-administrators-save-button"
          >
            {translate('securityGrouping:assignAdministrators.saveChanges')}
          </PrimaryButton>
        </PermissionGuard>
      </div>
    </div>
  );
};

export default AssignAdministratorsTab;
